import { create } from 'zustand';
import { supabase } from '../supabase';
import type {
  ScreenRecording,
  ScreenRecordingWithUrls,
  CreateScreenRecordingDraft,
} from '../types/screenRecording';

const BUCKET = 'screen-recordings';
const SIGNED_URL_TTL = 3600; // 1h

async function getUserContext() {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData.user) throw new Error('User not authenticated');

  const { data: profile, error } = await supabase
    .from('user_profiles')
    .select('organization_id')
    .eq('id', userData.user.id)
    .single();

  if (error || !profile?.organization_id) throw new Error('Organization not found');

  return { userId: userData.user.id, organizationId: profile.organization_id };
}

/**
 * Arricchisce le righe con thumbnail signed URL (batch) e video signed URL (lazy / opzionale).
 */
async function withSignedThumbnails(rows: ScreenRecording[]): Promise<ScreenRecordingWithUrls[]> {
  const paths = rows.map(r => r.thumbnail_path).filter((p): p is string => !!p);
  if (paths.length === 0) return rows.map(r => ({ ...r, thumbnail_url: null }));

  const { data } = await supabase.storage.from(BUCKET).createSignedUrls(paths, SIGNED_URL_TTL);
  const map = new Map<string, string>();
  data?.forEach(item => {
    if (item.path && item.signedUrl) map.set(item.path, item.signedUrl);
  });

  return rows.map(r => ({
    ...r,
    thumbnail_url: r.thumbnail_path ? map.get(r.thumbnail_path) ?? null : null,
  }));
}

interface ScreenRecordingsStore {
  mine: ScreenRecordingWithUrls[];
  teamShared: ScreenRecordingWithUrls[];
  loading: boolean;
  error: string | null;
  current: ScreenRecordingWithUrls | null;

  fetchMine: () => Promise<void>;
  fetchTeamShared: () => Promise<void>;
  fetchById: (id: string) => Promise<ScreenRecordingWithUrls | null>;

  createDraft: (draft: CreateScreenRecordingDraft) => Promise<ScreenRecording>;
  markReady: (id: string) => Promise<void>;
  markFailed: (id: string) => Promise<void>;
  rename: (id: string, title: string) => Promise<void>;
  toggleTeamShared: (id: string, value: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;

  getVideoSignedUrl: (storagePath: string) => Promise<string | null>;
  setCurrent: (rec: ScreenRecordingWithUrls | null) => void;

  subscribeRealtime: () => () => void;
}

export const useScreenRecordingsStore = create<ScreenRecordingsStore>((set, get) => ({
  mine: [],
  teamShared: [],
  loading: false,
  error: null,
  current: null,

  fetchMine: async () => {
    set({ loading: true, error: null });
    try {
      const { userId } = await getUserContext();
      const { data, error } = await supabase
        .from('screen_recordings')
        .select('*')
        .eq('user_id', userId)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;

      const enriched = await withSignedThumbnails((data || []) as ScreenRecording[]);
      set({ mine: enriched });
    } catch (err: any) {
      console.error('[screenRecordings] fetchMine error:', err);
      set({ error: err.message ?? 'Errore caricamento video' });
    } finally {
      set({ loading: false });
    }
  },

  fetchTeamShared: async () => {
    set({ loading: true, error: null });
    try {
      const { userId, organizationId } = await getUserContext();
      const { data, error } = await supabase
        .from('screen_recordings')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_team_shared', true)
        .neq('user_id', userId)
        .neq('status', 'deleted')
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;

      const enriched = await withSignedThumbnails((data || []) as ScreenRecording[]);
      set({ teamShared: enriched });
    } catch (err: any) {
      console.error('[screenRecordings] fetchTeamShared error:', err);
      set({ error: err.message ?? 'Errore caricamento video team' });
    } finally {
      set({ loading: false });
    }
  },

  fetchById: async (id) => {
    try {
      const { data, error } = await supabase
        .from('screen_recordings')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;

      const enriched = (await withSignedThumbnails([data as ScreenRecording]))[0];
      set({ current: enriched });
      return enriched;
    } catch (err: any) {
      console.error('[screenRecordings] fetchById error:', err);
      return null;
    }
  },

  createDraft: async (draft) => {
    const { userId, organizationId } = await getUserContext();
    const { data, error } = await supabase
      .from('screen_recordings')
      .insert({
        organization_id: organizationId,
        user_id: userId,
        title: draft.title ?? 'Nuova registrazione',
        recording_mode: draft.recording_mode,
        storage_path: draft.storage_path,
        thumbnail_path: draft.thumbnail_path,
        audio_storage_path: draft.audio_storage_path ?? null,
        mime_type: draft.mime_type,
        duration_seconds: draft.duration_seconds,
        size_bytes: draft.size_bytes,
        status: 'uploading',
      })
      .select()
      .single();
    if (error) throw error;
    return data as ScreenRecording;
  },

  markReady: async (id) => {
    const { error } = await supabase
      .from('screen_recordings')
      .update({ status: 'ready' })
      .eq('id', id);
    if (error) throw error;
    // Refresh locale: la riga aggiornata torna nella lista "mine"
    await get().fetchMine();
  },

  markFailed: async (id) => {
    const { error } = await supabase
      .from('screen_recordings')
      .update({ status: 'failed' })
      .eq('id', id);
    if (error) console.error('[screenRecordings] markFailed error:', error);
  },

  rename: async (id, title) => {
    const trimmed = title.trim() || 'Nuova registrazione';
    const { error } = await supabase
      .from('screen_recordings')
      .update({ title: trimmed })
      .eq('id', id);
    if (error) throw error;
    set((state) => ({
      mine: state.mine.map(r => r.id === id ? { ...r, title: trimmed } : r),
      current: state.current?.id === id ? { ...state.current, title: trimmed } : state.current,
    }));
  },

  toggleTeamShared: async (id, value) => {
    const { error } = await supabase
      .from('screen_recordings')
      .update({ is_team_shared: value })
      .eq('id', id);
    if (error) throw error;
    set((state) => ({
      mine: state.mine.map(r => r.id === id ? { ...r, is_team_shared: value } : r),
      current: state.current?.id === id ? { ...state.current, is_team_shared: value } : state.current,
    }));
  },

  remove: async (id) => {
    const rec = get().mine.find(r => r.id === id) ?? get().current;
    if (!rec) {
      // Recupero minimal per ottenere i path
      const { data } = await supabase
        .from('screen_recordings')
        .select('storage_path,thumbnail_path,audio_storage_path')
        .eq('id', id)
        .single();
      if (data) {
        const paths = [data.storage_path, data.thumbnail_path, data.audio_storage_path].filter((p): p is string => !!p);
        if (paths.length > 0) await supabase.storage.from(BUCKET).remove(paths);
      }
    } else {
      const paths = [rec.storage_path, rec.thumbnail_path, rec.audio_storage_path].filter((p): p is string => !!p);
      if (paths.length > 0) await supabase.storage.from(BUCKET).remove(paths);
    }

    const { error } = await supabase
      .from('screen_recordings')
      .delete()
      .eq('id', id);
    if (error) throw error;

    set((state) => ({
      mine: state.mine.filter(r => r.id !== id),
      current: state.current?.id === id ? null : state.current,
    }));
  },

  getVideoSignedUrl: async (storagePath) => {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrl(storagePath, SIGNED_URL_TTL);
    if (error || !data?.signedUrl) {
      console.error('[screenRecordings] getVideoSignedUrl error:', error);
      return null;
    }
    return data.signedUrl;
  },

  setCurrent: (rec) => set({ current: rec }),

  subscribeRealtime: () => {
    let channel: ReturnType<typeof supabase.channel> | null = null;
    let cancelled = false;

    (async () => {
      try {
        const { userId } = await getUserContext();
        if (cancelled) return;

        channel = supabase
          .channel(`screen_recordings_${userId}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'screen_recordings',
              filter: `user_id=eq.${userId}`,
            },
            () => {
              // Re-fetch the whole list on any change. Cheap, idempotent, race-safe.
              get().fetchMine().catch(() => undefined);
            }
          )
          .subscribe();
      } catch (err) {
        console.warn('[screenRecordings] subscribeRealtime failed (non-fatal):', err);
      }
    })();

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  },
}));
